<template>
    <!-- TODO translate -->
    <a
        :class="classIntercomLauncher"
        :href="`mailto:${appId}@incoming.intercom.io`"
        :title="'Chat with us'"
        class="intercom-launcher"
        aria-live="polite"
        @click="onClick"
    >
        <div class="icon close"></div>
        <div class="icon open"></div>
        <span
            :class="classUnreadCount"
            class="unread-count"
            :title="t('intercom.messages_count', unreadCount)"
        ></span>
    </a>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useIntercomLauncher } from './useIntercomLauncher'
import { useI18n } from 'vue-i18n'
import { getTimeout } from '@/helpers/globals.ts'

const { appId, isLoaded, unreadCount, chatStarted, show, loadIntercom } =
    useIntercomLauncher()

const { t } = useI18n()
if (!import.meta.env.SSR && chatStarted.value) {
    show()
}

const timeout = getTimeout()

if (timeout) {
    const { setTimeout } = timeout

    setTimeout(async () => {
        if (isLoaded.value) {
            return
        }

        // When consultants are online
        // Chat pops up by itself
        // We're delaying this on purpose due to Lighthouse scoring
        try {
            await loadIntercom()
        } catch {
            // do nothing
        }
    }, 6_000)
}

function onClick(e: MouseEvent): void {
    if (!isLoaded.value) {
        e.preventDefault()
        show()
    }
}

const classIntercomLauncher = computed(() => ({
    'launcher-visible': isLoaded.value && chatStarted.value !== undefined,
    'chat-open': chatStarted.value,
}))

const classUnreadCount = computed(() => ({
    'active': unreadCount.value > 0,
}))
</script>

<style lang="scss" scoped>
$launcher-color: #334e80;
$launcher-size: 50px;
$launcher-count-size: 20px;
$launcher-count-color: #fc576b;

.intercom-launcher {
    backface-visibility: hidden;
    background: $launcher-color;
    border-radius: 50%;
    bottom: 10px;
    box-shadow:
        0 1px 6px rgb(0 0 0 / 6%),
        0 2px 32px rgb(0 0 0 / 16%);
    cursor: pointer;
    outline: 0;
    position: fixed;
    right: 10px;
    transform-origin: center;
    transition:
        opacity 0.25s ease,
        transform 0.25s ease;
    z-index: 99;

    &:focus {
        outline: 0;
    }

    height: $launcher-size;
    opacity: 1;
    transform: scale(1);
    width: $launcher-size;

    &.launcher-visible {
        height: $launcher-size;
        opacity: 1;
        transform: scale(1);
        width: $launcher-size;
    }

    &.chat-open {
        z-index: 2000;
    }
}

.icon {
    background-position: 50%;
    background-repeat: no-repeat;
    border-radius: 50%;
    bottom: 0;
    position: absolute;
    top: 0;
    transition:
        transform 0.16s linear,
        opacity 0.08s linear;
    width: 100%;

    &.open {
        background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjgiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAyOCAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzEzMjczXzQ5MzUpIj4KPHBhdGggZD0iTTI4IDMyQzI4IDMyIDIzLjI4NiAzMC4xNDUgMTkuNDczIDI4LjY2SDMuNDM3QzEuNTQgMjguNjYgMCAyNy4wMjYgMCAyNS4wMTNWMy42NDRDMCAxLjYzMyAxLjU0IDAgMy40MzcgMEgyNC41NjJDMjYuNDYgMCAyNy45OTkgMS42MzIgMjcuOTk5IDMuNjQ1VjIyLjA0OUwyOCAzMlpNMjMuODYxIDIwLjAxOEMyMy43ODYxIDE5LjkyMTkgMjMuNjkyMSAxOS44NDIzIDIzLjU4NDggMTkuNzg0NEMyMy40Nzc2IDE5LjcyNjYgMjMuMzU5NSAxOS42OTE2IDIzLjIzODEgMTkuNjgxN0MyMy4xMTY2IDE5LjY3MTggMjIuOTk0NCAxOS42ODczIDIyLjg3OTIgMTkuNzI3MUMyMi43NjQxIDE5Ljc2NjkgMjIuNjU4NCAxOS44MzAyIDIyLjU2OSAxOS45MTNDMjIuNTM5IDE5LjkzOSAxOS41NTQgMjIuNTk0IDEzLjk5OSAyMi41OTRDOC41MTMgMjIuNTk0IDUuNDgyIDE5Ljk1OCA1LjQyOCAxOS45MUM1LjMzODQ3IDE5LjgyNzcgNS4yMzI4NSAxOS43NjQ4IDUuMTE3OCAxOS43MjUzQzUuMDAyNzUgMTkuNjg1OSA0Ljg4MDc2IDE5LjY3MDcgNC43NTk1NCAxOS42ODA3QzQuNjM4MzMgMTkuNjkwOCA0LjUyMDUxIDE5LjcyNTggNC40MTM1MyAxOS43ODM3QzQuMzA2NTUgMTkuODQxNiA0LjIxMjc0IDE5LjkyMSA0LjEzOCAyMC4wMTdDMy45Nzc5MyAyMC4yMTY3IDMuODk5NjUgMjAuNDY5OCAzLjkxOSAyMC43MjVDMy45MzU5IDIwLjk3ODkgNC4wNDk3NSAyMS4yMTY3IDQuMjM3IDIxLjM4OUM0LjM3OSAyMS41MTcgNy43NzQgMjQuNTM5IDEzLjk5OSAyNC41MzlDMjAuMjI1IDI0LjUzOSAyMy42MiAyMS41MTcgMjMuNzYyIDIxLjM4OUMyMy45NDg5IDIxLjIxNjUgMjQuMDYyNCAyMC45Nzg4IDI0LjA3OSAyMC43MjVDMjQuMDk4NCAyMC40NzAyIDI0LjAyMDUgMjAuMjE3NiAyMy44NjEgMjAuMDE4WiIgZmlsbD0id2hpdGUiLz4KPC9nPgo8ZGVmcz4KPGNsaXBQYXRoIGlkPSJjbGlwMF8xMzI3M180OTM1Ij4KPHJlY3Qgd2lkdGg9IjI4IiBoZWlnaHQ9IjMyIiBmaWxsPSJ3aGl0ZSIvPgo8L2NsaXBQYXRoPgo8L2RlZnM+Cjwvc3ZnPgo=');
        background-position: 50% 60%;
        background-size: 54% 60%;
        opacity: 1;
        transform: rotate(0deg) scale(1);

        .chat-open & {
            opacity: 0;
            transform: rotate(30deg) scale(0);
        }
    }

    &.close {
        background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAcBAMAAACAI8KnAAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAIVBMVEUAAAD///////////////////////////////////8AAADPn83rAAAACXRSTlMACq47u/I8r7wWzHxoAAAAAWJLR0QAiAUdSAAAAAlwSFlzAAALEgAACxIB0t1+/AAAAJJJREFUGNNdzzEKhDAQBdAvwtYWW9hbbSdCDrBnWBDS2Sx7A8HSKwgic1tNxj/jmirDC5P/UTSw01V4ri2nMr7xkg/HIAu+Qi6j9HhEcpB1gHFGGCuSTyQTlQ2Vg3ic4x49TVpzwcQXvI+3x/+r0p9eLAfyYhrIWNOSmfZkVlh2Kpm9Z+bJeh68oSYmnlGMnv1X7RZ2SET5id+LAAAAAElFTkSuQmCC');
        background-size: 25%;
        opacity: 0;
        transform: rotate(-30deg);

        .chat-open & {
            opacity: 1;
            transform: rotate(0deg);
        }
    }
}

.unread-count {
    backface-visibility: hidden;
    background: $launcher-count-color;
    border-radius: calc($launcher-count-size / 2);
    box-shadow: 0 1px 0 rgb(0 0 0 / 15%);
    box-sizing: border-box;
    color: #fff;
    font-size: $launcher-count-size * 0.6;
    height: $launcher-count-size;
    line-height: $launcher-count-size;
    min-width: $launcher-count-size;
    opacity: 0;
    padding: 0 4px;
    position: absolute;
    right: -2px;
    text-align: center;
    top: -2px;
    transform: scale(0.5);
    transform-origin: center;
    transition:
        opacity 0.15s ease,
        transform 0.15s ease;

    &.active {
        opacity: 1;
        transform: scale(1);
    }
}
</style>
